<template>
  <div>
    <div 
      v-if="canShow && showActions" 
      class="blog-actions-container"
    >
      <!-- Actions dropdown -->
      <b-dropdown
        v-if="isMobile"
        id="dropdown-dropleft"
        variant="link"
        right
        class="d-block d-lg-none position-absolute"
        no-caret
        style="top: 102px; right: -5px"
      >
        <template #button-content>
          <feather-icon
            icon="MoreVerticalIcon"
            size="20"
            class="text-primary mt-25 mt-lg-0 mr-n50 mr-md-50 pr-lg-0 d-block"
          />
        </template>
        <b-dropdown-text class="bg-light">
          <p class="user-name font-weight-bolder mb-0">
            {{ $t("settings.details.title") }}
          </p>
        </b-dropdown-text>
        <b-dropdown-item v-if="canEdit" @click="editing = true">
          {{ $t("settings.details.edit") }}
        </b-dropdown-item>
        <b-dropdown-item v-if="canDelete" @click="isUnshareModalVisible = true">
          {{ $t("settings.details.delete") }}
        </b-dropdown-item>
        <b-dropdown-item
          v-if="canShare"
          class="d-block"
          @click="isShareModalVisible = true"
        >
          {{ $t("settings.details.share") }}
        </b-dropdown-item>
      </b-dropdown>

      <!-- Actions -->
      <div v-else class="float-right mr-2 d-none d-lg-block" style="width: 200px">
        <div class="mt-n3 position-absolute" style="width: 200px">
          <!-- Normal mode: edit, share, delete buttons -->
          <div v-if="!editing" class="normal-mode-actions">
            <feather-icon
              v-if="canEdit"
              v-b-tooltip.hover.bottom
              icon="Edit2Icon"
              :title="$t('tooltips.crud.edit')"
              size="20"
              class="text-primary edit-icon float-right ml-1"
              role="button"
              @click="editing = !editing"
            />
            <feather-icon
              v-if="canShare"
              v-b-tooltip.hover.bottom
              icon="Share2Icon"
              :title="$t('tooltips.crud.share')"
              size="20"
              class="text-primary edit-icon float-right ml-1"
              role="button"
              @click="isShareModalVisible = true"
            />
            <feather-icon
              v-if="canDelete"
              v-b-tooltip.hover.bottom
              icon="Trash2Icon"
              size="20"
              :title="$t('tooltips.crud.delete')"
              class="text-primary edit-icon float-right ml-1"
              role="button"
              @click="isUnshareModalVisible = true"
            />
          </div>
          <!-- Edit mode: cancel and save buttons -->
          <div v-else class="edit-mode-actions">
            <feather-icon
              icon="XCircleIcon"
              size="20"
              class="text-primary edit-icon float-right ml-1"
              role="button"
              @click="editing = !editing"
            />
            <b-button
              class="float-right mt-n50"
              variant="primary"
              :disabled="editSlug == ''"
              @click="handleEditItem"
            >
              {{ $t("articles.save-changes") }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Delete item modal -->
      <unshare-item-modal
        v-model="isUnshareModalVisible"
        model-type="blog"
        @remove-item="deleteItem(item)"
        @modal-invisible="isUnshareModalVisible = false"
      />

      <!-- Share item modal -->
      <share-modal
        v-if="item && item.title"
        v-model="isShareModalVisible"
        :can-share="canShare"
        :item-name="translateTranslationTable(currentLocale, item.title)"
        model-type="blog"
        type="blogs"
        :model-key="item.key"
      />
    </div>

    <!-- Blog container -->
    <div class="blog-container" :class="isMobile ? 'my-2' : 'px-4 my-3'">
      <b-spinner
        v-if="isLoading"
        class="center-x"
        small
        variant="primary"
        type="grow"
      />
      <content-with-sidebar
        v-if="canShow"
        class="cws-container cws-sidebar-right blog-wrapper"
      >
        <!-- content -->
        <div class="blog-detail-wrapper">
          <b-row class="justify-content-md-center">
            <!-- blogs -->
            <b-col cols="12">
              <b-card
                no-body
                style="overflow: hidden"
                img-alt="Blog Detail Pic"
              >
                <div class="mb-2 w-100">
                  <img
                    class="w-100 imagen"
                    :src="item.bannerURL || item.imageURL || placeholder"
                  >
                  <input
                    ref="file"
                    type="file"
                    style="display: none"
                    @change="onFileChange"
                  >
                  <div 
                    v-if="canEdit && editing" 
                    v-b-modal.modal-edit-project 
                    class="card-imgage-edit"
                  >
                    <small class="text-white">
                      {{ $t('blogs.edit-image') }}
                    </small>
                    <feather-icon
                      icon="Edit2Icon"
                      size="20"
                      class="text-white edit-icon float-right ml-50"
                      role="button"
                    />
                  </div>
                </div>
                <b-modal
                  id="modal-edit-project"
                  :title="$t('blogs.edit-image')"
                  cancel-variant="outline-secondary"
                  :ok-title="$t('form.actions.save')"
                  :cancel-title="$t('form-create-item.cancel')"
                  centered
                  size="lg"
                >
                  <b-row>
                    <b-col color="12" md="6">
                      <b-form-group :label="$t('backoffice.onboarding-form.form.image')" label-for="image" class="d-inline-block">
                        <file-preview
                          v-if="editForm.imageURL"
                          :src="getImageResource(editForm.imageURL)"
                          mime-type="image"
                          cancelable

                          @cancel="editForm.imageURL = null"
                        />
                        <file-upload
                          v-else
                          v-model="imageURL"
                          type="image"
                          cancelable
                          :placeholder="$t('form-create-item.image-button')"
                          :drop-placeholder="$t('form-create-item.drop-placeholder')"
                          @cancel="imageURL = null"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col color="12" md="6">
                      <b-form-group label="Banner" label-for="banner" class="d-inline-block">
                        <file-preview
                          v-if="editForm.bannerURL"
                          :src="getImageResource(editForm.bannerURL)"
                          mime-type="image"
                          cancelable

                          @cancel="editForm.bannerURL = null"
                        />
                        <file-upload
                          v-else
                          v-model="bannerURL"
                          type="image"
                          cancelable
                          :placeholder="$t('form-create-item.image-button')"
                          :drop-placeholder="$t('form-create-item.drop-placeholder')"
                          @cancel="bannerURL = null"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-modal>

                <b-input-group 
                  v-if="editing" 
                  :class="isMobile?'px-2':'px-3'" 
                  class="mb-1"
                >
                  <template #prepend>
                    <b-input-group-text class="bg-light">
                      {{ `${currentUrl}/` }}
                    </b-input-group-text>
                  </template>
                  <b-form-input
                    v-model="editSlug"
                    :state="editSlug != ''"
                    :placeholder="$t('form-create-item.slug')"
                  />
                </b-input-group>
                <div :class="isMobile?'px-2':'px-3'">
                  <h1 v-if="!editing">
                    {{
                      translateTranslationTable(currentLocale, item.title)
                    }}
                  </h1>
                  <b-form-input
                    v-else
                    v-model="editForm.title"
                    class="edit-title"
                    :placeholder="
                      translateTranslationTable(currentLocale, item.title)
                    "
                  />
                  <h4 v-if="item.headline && !editing" class="html-text-ellipsis-5 mb-1">
                    {{ item.headline[currentLocale] || Object.values(item.headline)[0] }}
                  </h4>
                  <b-form-input
                    v-if="editing"
                    v-model="editForm.headline"
                    :placeholder="$t('form-create-item.headline-placeholder')"
                    class="mt-1"
                  />
                  <b-media no-body>
                    <!-- Avatar -->

                    <!-- Owner and date-->
                    <b-media-body v-if="item.poweredBy" :class="isMobile?'mb-2':'mb-3'">
                      <small class="text-muted mr-50">{{ $t("blogs.by") }}</small>
                      <small>
                        <b-link class="text-body">
                          {{ translate(item.poweredBy.name) }}
                        </b-link>
                      </small>
                      <span class="text-muted ml-75 mr-50">|</span>
                      <small v-if="!editing" class="text-muted">
                        {{ timestampToTime(item.date) }}
                      </small>
                      <VueCtkDateTimePicker
                        v-else
                        id="date"
                        v-model="date"
                        :value="date"
                        :locale="$store.state.locale.currentLocale"
                        format="YYYY-MM-DD"
                        formatted="ll"
                        :label="$t('backoffice.availability.date-no-slots')"
                        color="var(--primary)"
                        no-button-now
                        only-date
                        button-color="var(--primary)"
                      />
                    </b-media-body>
                  </b-media>
                </div>

                <!-- Description -->
                <div
                  v-if="item.content && !editing"
                  class="blog-content content-container"
                  :class="isMobile?'mx-2 mb-1':'mx-3 mb-3'"
                  v-html="
                    item.content[currentLocale] ||
                      Object.values(item.content)[0]
                  "
                />
                <vue-editor
                  v-if="editing"
                  v-model="editForm.description"
                  class="float-left"
                  :class="isMobile?'mx-2 mb-1':'mx-3 mb-3'"
                />

                <!-- user creator info -->
                <b-media
                  v-if="item.poweredBy"
                  class="d-flex align-items-center"
                  :class="isMobile?'ml-2 mr-2':'ml-3 mr-3'"
                  no-body
                >
                  <b-media-aside>
                    <b-avatar size="24" :src="item.poweredBy.logoURL" />
                  </b-media-aside>
                  <b-media-body>
                    <h6 class="font-weight-bolder mb-0">
                      {{ translate(item.poweredBy.name) }}
                    </h6>
                    <b-card-text>
                      <p
                        v-if="item.poweredBy.headline"

                        class="html-text-ellipsis-2 mb-0"
                        v-html="translate(item.poweredBy.headline)"
                      />
                      <p
                        v-if="!item.poweredBy.headline && item.poweredBy.description"
                        class="html-text-ellipsis-2 mb-0"
                        v-html="
                          translate(item.poweredBy.description)
                        "
                      />
                    </b-card-text>
                  </b-media-body>
                </b-media>

                <hr class="my-2">

                <!-- Icons -->
                <div
                  class="d-flex align-items-center justify-content-between mb-2"
                  :class="isMobile?'':'mx-2'"
                >
                  <div class="d-flex align-items-center">
                    <div class="d-flex align-items-center ml-2">
                      <span class="mr-1">
                        <feather-icon
                          role="button"
                          size="20"
                          :class="
                            item && item.likedByMember
                              ? 'profile-likes'
                              : 'profile-icon'
                          "
                          icon="HeartIcon"
                          class="mr-25"
                          @click="toggleLike"
                        />
                        {{ item.totalLikes || 0 }}
                      </span>
                      <span>
                        <feather-icon
                          role="button"
                          size="20"
                          icon="MessageSquareIcon"
                          class="mr-25"
                          @click="scrollBottom"
                        />
                        {{ item.commentCount || 0 }}
                      </span>
                    </div>
                  </div>
                </div>
              </b-card>
            </b-col>
            <!--/ blogs -->

            <!-- blog comments section -->
            <b-col
              v-if="showComments"
              id="blogComment"
              cols="12"
              class="mt-2"
            >

              <!-- Comments section -->
              <comments-section
                ref="commentsSection"
                :source-key="item.key"
                :can-moderate="canModerateComments"
                morph-type="blogs"
                item-type="comments"
                :leaf="false"
                @comment-created="afterCommentCreation"
                @comment-deleted="deleteComment"
              />
            </b-col>
          </b-row>
        </div>

        <!-- Sidebar, relate blogs -->
        <div
          v-if="showSidebar"
          slot="sidebar"
          class="blog-sidebar py-lg-0"
        >
          <div class="blog-recent-posts">
            <h6 class="section-label mb-1">
              {{ $t("blogs.recent-posts") }}
            </h6>
            <b-media
              v-for="(recentpost, index) in recentPosts"
              v-show="hasRecentPosts"
              :key="recentpost.img"
              no-body
              :class="index ? 'mt-2' : ''"
            >
              <b-media-aside class="mr-2">
                <b-link :to="{ name: 'blog', params: { id: recentpost.key } }">
                  <b-img
                    :src="recentpost.bannerURL ||recentpost.imageURL || placeholder"
                    rounded
                    style="width: 100px; height: 70px; object-fit: contain;"
                  />
                </b-link>
              </b-media-aside>
              <b-media-body>
                <h6 class="blog-recent-post-title mb-0">
                  <b-link
                    class="text-body-heading html-text-ellipsis-2"
                    :to="{ name: 'blog', params: { id: recentpost.key } }"
                  >
                    {{
                      recentpost.title[currentLocale] ||
                        Object.values(recentpost.title)[0]
                    }}
                    <small v-if="recentpost.headline"><p class="mb-25">{{
                      recentpost.headline[currentLocale] ||
                        Object.values(recentpost.headline)[0]
                    }}</p>
                    </small>
                  </b-link>
                </h6>
                <span class="text-muted mb-0">
                  {{ timestampToTime(recentpost.date) }}
                </span>
              </b-media-body>
            </b-media>
            
            <b-row v-if="!hasRecentPosts" class="horizontal-placeholder">
              <b-col cols="12">
                <img :src="blogPlaceholder">
              </b-col>
              <b-col cols="12">
                <p class="text-primary">
                  {{ $t("blogs.recent-posts-placeholder") }}
                </p>
              </b-col>
            </b-row>

          </div>
        </div>
      </content-with-sidebar>
    </div>
  </div>
</template>

<script>

import Ripple from 'vue-ripple-directive';
import ToastificationContentVue from '@/@core/components/toastification/ToastificationContent.vue';
import Placeholder from '@/assets/images/placeholders/light/placeholder-dark.jpeg';
import ContentWithSidebar from '@core/layouts/components/content-with-sidebar/ContentWithSidebar.vue';
import moment from 'moment';
import { getImageResource } from '@/@core/utils/image-utils';
import { VueEditor } from 'vue2-editor';
import FormTranslationTable from '@core/components/form-translation-table/FormTranslationTable.vue';
import vSelect from 'vue-select';
import { checkPermissions } from '@/@core/utils/roles-utils';
import UnshareItemModal from '@/@core/components/modal/UnshareItemModal.vue';
import ShareModal from '@/@core/components/modal/ShareModal.vue';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import BlogPlaceholder from '@/assets/images/placeholders/light/blog.svg';
import Vue from 'vue';
import FileUpload from '@core/components/files/FileUpload.vue';
import FilePreview from '@core/components/files/FilePreview.vue';
import { getCollectiveUrl } from '@/@core/utils/collective';
import { CommentsSection } from '@/views/apps/comments/components';

import { formatExactDateTime } from '@/utils/dateFormatter';

export default {
  name: 'BlogDetails',
  components: {
    ContentWithSidebar,
    VueEditor,
    FormTranslationTable,
    vSelect,
    UnshareItemModal,
    ShareModal,
    FileUpload,
    FilePreview,
    CommentsSection,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: true,
      isClassifiersModalVisible: false,
      isCustomFieldsModalVisible: false,
      imageSrc: null,
      imageURL: null,
      bannerURL: null,
      editing: false,
      editForm: {
        title: '', description: '', headline: '', bannerURL: null, imageURL: null,
      },
      itemImage: null,
      editedItem: {},
      lastLoadedPage: 1,
      isUnshareModalVisible: false,
      isShareModalVisible: false,
      date: '',
      item: {},
      editSlug: '',
    };
  },
  computed: {
    blogTitle() {
      const app = Object.values((this.$store.getters.apps?.apps) || {}).find((x) => x.key === 'blogs');
      if (app?.customizationName) {
        return translateTranslationTable(this.currentLocale, app.customizationName);
      }
      if (app?.name) {
        return translateTranslationTable(this.currentLocale, app.name);
      }
      return this.$t('blogs.title');
    },
    id() {
      return this.$route.params.id;
    },
    isMobile() {
      return window.innerWidth < 576;
    },
    blogPlaceholder() {
      return BlogPlaceholder;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    loggedUser() {
      return this.$store.getters.loggedUser;
    },
    collective() {
      return this.$store.state.collective.collective;
    },
    loggedMemberRoles() {
      return this.$store.getters.loggedMemberRoles;
    },
    showComments() {
      // TODO: in the future we can add a setting to enable/disable comments
      return true;
    },
    showActions() {
      // console.log('showActions...');
      const show = this.canEdit || this.canDelete || this.canShare || this.isOwner || this.isCreator;
      // console.log('isCreator', this.isCreator);
      // console.log('show', show);
      return show;
    },
    isOwner() {
      return this.loggedUser.key === this.item?.ownedByUser?.key;
    },
    loggedMember() {
      return this.$store.getters.loggedMember;
    },
    isCreator() {
      // console.log('loggedMember?.key', this.loggedMember?.key);
      // console.log('item', this.item);
      return this.loggedMember?.key && this.loggedMember?.key === this.item?.createdByMember?.key;
    },
    canShow() {
      return this.item && !this.isLoading;
    },
    canEdit() {
      return (
        this.isOwner
        || this.isStaff
        || this.isCreator
        || checkPermissions(
          'update',
          'blog',
          this.loggedMemberRoles,
          this.collective,
        )
      );
    },
    canDelete() {
      return (
        this.isOwner
        || this.isStaff
        || this.isCreator
        || checkPermissions(
          'delete',
          'blog',
          this.loggedMemberRoles,
          this.collective,
        )
      );
    },
    canShare() {
      return (
        this.isOwner
        || this.isStaff
        || this.isCreator
        || checkPermissions(
          'share',
          'blog',
          this.loggedMemberRoles,
          this.collective,
        )
      );
    },
    canModerateComments() {
      return this.isStaff || checkPermissions(
        'manage',
        'comment',
        this.loggedMemberRoles,
        this.collective,
      );
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    comments() {
      const comments = this.$store.getters.comments[this.item?.key]?.unpaginated || [];
      return comments;
    },
    recentPosts() {
      const posts = this.$store.getters.blogs?.recent?.unpaginated || [];
      const selectedPosts = posts?.filter((post) => post.key !== this.item.key);
      this.updateBreadcrumbs();
      return selectedPosts?.slice(0, 6);
    },
    recentPostsNumber() {
      return this.recentPosts.length;
    },
    hasRecentPosts() {
      return this.recentPostsNumber > 0;
    },
    showSidebar() {
      return !this.isMobile || this.hasRecentPosts;
    },
    placeholder() {
      return Placeholder;
    },
    classifiersData() {
      if (this.$store.getters.classifiers.blog) {
        return this.$store.getters.classifiers.blog.unpaginated;
      }
      return [];
    },
    classifiers() {
      return this.classifiersData;
    },
    currentUrl() {
      return getCollectiveUrl(this.collective, '/blogs');
      // return `${process.env.VUE_APP_COMMUNITY_TEMPLATE_URL.replace(
      //   '{subdomain}',
      //   this.collective.subdomain || 'app',
      // ).replace('{slug}', this.collective.slug)}/blogs`;
    },
  },
  watch: {
    'item.date': {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.date = moment(newValue).format('YYYY-MM-DD');
        }
      },
    },
    item(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchComments();
      }
    },
    itemImage(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.base64Encode(newValue)
            .then((value) => {
              this.imageSrc = value;
            })
            .catch(() => {
              this.imageSrc = null;
            });
        } else {
          this.imageSrc = null;
        }
      }
    },
    editSlug(value) {
      return value;
    },
  },
  async created() {
    await this.fetchItem(this.id);
    await this.fetchRecentBlogs();
    await this.fetchComments();
    this.updateBreadcrumbs();
  },
  methods: {
    translate(field) {
      return translateTranslationTable(this.currentLocale, field);
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.itemImage = files[0];
    },
    base64Encode(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    async fetchComments() {
      await this.$store.dispatch('getItems', {
        itemType: 'comments',
        storedKey: this.item?.key,
        page: this.lastLoadedPage,
        perPage: 16,
        forceAPICall: true,
        requestConfig: {
          morphType: 'blogs',
          key: this.item?.key,
        },
      });
      this.isLoading = false;
    },
    async fetchItem(id) {
      const url = Vue.$cookies.get('type');
      const res = await this.$store.dispatch('getItems', {
        itemType: 'blogs',
        storedKey: 'item',
        forceAPICall: true,
        requestConfig: {
          reduced: 0,
          ...(url !== 'slug' && id.replace(/[^0-9]/g, '').length > 10 ? { blogKey: id } : { slug: id }),
        },
      });
      this.item = res?.data[0];
      this.editForm = { ...this.item };
      this.editForm.title = this.translate(this.item?.title);
      this.editSlug = this.translate(this.item?.slug || this.item?.key);
      this.editForm.description = this.translate(this.item?.content);
      this.editForm.headline = this.translate(this.item?.headline);
      Vue.$cookies.remove('type');
    },
    async fetchBlogDetails() {
      if (!this.item?.key) return;
      
      const res = await this.$store.dispatch('getItems', {
        itemType: 'blogs',
        storedKey: 'fullItem',
        forceAPICall: true,
        requestConfig: {
          blogKey: this.item.key,
          reduced: 0
        },
      });
      
      if (res?.data?.[0]) {
        this.item = { ...this.item, ...res.data[0] };
      }
    },
    async fetchRecentBlogs() {
      await this.$store.dispatch('getItems', {
        itemType: 'blogs',
        storedKey: 'recent',
        perPage: 250,
        page: this.lastLoadedPage,
        requestConfig: {
          orderByDate: -1,
        },
      });
    },
    timestampToTime(timestamp) {
      return formatExactDateTime(timestamp, this.currentLocale);
    },
    getImageSrc(url) {
      return getImageResource(url);
    },
    async fetchClassifiers() {
      await this.$store.dispatch('getItems', {
        storedKey: 'blog',
        itemType: 'classifiers',
        forceAPICall: true,
        page: 1,
        requestConfig: {
          blogKey: this.item?.key,
        },
      });

      this.isLoading = false;
    },
    async toggleLike() {
      await this.$store.dispatch('toggleLike', {
        itemType: 'blogs',
        morphType: 'blogs',
        key: this.item?.key,
      });
    },
    async scrollBottom() {
      // Use nextTick to ensure DOM is updated
      await this.$nextTick();
      
      // Try to find the comment form element
      const element = document.getElementById('commentForm');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      
      // Fallback: try to scroll to the comment form using ref
      if (this.$refs.commentForm) {
        this.$refs.commentForm.$el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      
      // If no comment form exists, scroll to the comments section
      const commentsSection = document.getElementById('blogComment');
      if (commentsSection) {
        commentsSection.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      
      // Final fallback: scroll to bottom of page
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    },
    async afterCommentCreation() {
      this.item.commentCount++;
      // Refresh comments list reactively
      if (
        this.$refs.commentsSection &&
        this.$refs.commentsSection.$refs &&
        this.$refs.commentsSection.$refs.commentsList &&
        typeof this.$refs.commentsSection.$refs.commentsList.fetchData === 'function'
      ) {
        await this.$refs.commentsSection.$refs.commentsList.fetchData();
      }
    },

    // REMOVE COMMENT
    async deleteComment() {
      if (this.item.commentCount > 0) {
        this.item.commentCount--;
      }
    },

    async deleteItem(item) {
      try {
        await this.$store.dispatch('deleteComment', {
          item: {
            itemType: 'blogs',
            requestConfig: {
              blogKey: item.key,
            },
          },
        });
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: this.$t('unshare.toast-messages.success', { item: 'Blog' }),
            icon: 'AlertTriangleIcon',
            variant: 'success',
          },
        });
        this.isUnshareModalVisible = false;
        this.$router.push(`/${item.poweredBy.slug}/blogs`);
      } catch {}
    },
    getImageResource(image) {
      return getImageResource(image);
    },
    async handleEditItem() {
      try {
        const editedTitle = {};
        const editedContent = {};
        const editedHeadline = {};
        const file = {};
        const itemDelete = {};
        editedTitle[this.currentLocale] = this.editForm.title;
        editedContent[this.currentLocale] = this.editForm.description;
        editedHeadline[this.currentLocale] = this.editForm.headline;
        if (this.imageURL) {
          file.imageURL = this.imageURL;
        } else if (this.editForm.imageURL !== null) {
          file.imageURL = this.editForm.imageURL;
        } else {
          file.imageURL = null;
          itemDelete.deleteImageURL = true;
        }
        if (this.bannerURL) {
          file.bannerURL = this.bannerURL;
        } else if (this.editForm.bannerURL !== null) {
          file.bannerURL = this.editForm.bannerURL;
        } else {
          file.bannerURL = null;
          itemDelete.deleteBannerURL = true;
        }
        const result = await this.$store.dispatch('editItem', {
          item: {
            itemType: 'blogs',
            requestConfig: {
              blogKey: this.item.key,
              title: editedTitle,
              headline: editedHeadline,
              content: editedContent,
              date: this.date,
              ...itemDelete,
              ...((this.editSlug != this.item.key && this.editSlug != this.item.slug) ? { slug: this.validSlug(this.editSlug) } : ''),
            },
          },
          file,
        });
        this.item = result;
        this.editedItem = result;
        this.updateBreadcrumbs();
        this.editing = false;
        this.imageSrc = null;
        this.itemImage = null;
        this.bannerImage = null;
        if (this.editSlug != this.item.key && this.validSlug(this.editSlug) != this.item.slug) {
          setTimeout(() => {
            Vue.$cookies.set('type', 'slug');
            window.location = `${this.currentUrl}/${this.validSlug(this.editSlug)}`;
          }, 800);
        }
      } catch (e) {
        console.log(e.message)
        this.$toast({
          component: ToastificationContentVue,
          props: {
            title: this.$t('blogs.slug-error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    validSlug(slug) {
      return slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '-');
    },
    // TODO: move to utils
    updateBreadcrumbs() {
      const title = this.item?.title ?? this.item?.name;
      const breadcrumbs = [
        {
          text: this.blogTitle || 'blogs.breadcrumb-text.blogs-list',
          to: { name: 'blogs' },
        },
        {
          text:
          translateTranslationTable(this.currentLocale, title)
            || 'blogs.breadcrumb-text.blog-details',
          active: true,
        },
      ];
      this.$store.commit('app/SET_BREADCRUMBS', breadcrumbs);
    },
    translateTranslationTable,
  },
};
</script>
<style>
.blog-container {
  max-width: 1600px;
  margin: 0 auto;
}
.cws-container .cws-content {
  width: calc(100% - 460px - 2rem);
}
.cws-container .cws-sidebar {
  width: 460px;
}

.imagen {
  max-height: 500px !important;
  object-fit: cover !important;
  overflow: hidden;
}

.edit-title {
  font-size: 1.5rem;
  color: #5e5873;
  font-weight: 500;
}
.ql-container .ql-editor span {
  font-family: "Montserrat", Helvetica, Arial, serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
}

.ql-container .ql-editor p {
  font-family: "Montserrat", Helvetica, Arial, serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  color: black;
}
.form-file-custom {
  position: absolute;
  top: 8px;
  right: 2%;
}
.card-imgage-edit {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 0.75rem;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.2);
  height: 40px;
}
.card-imgage-edit svg {
  color: #f0f0f0 !important;
}
.content-container img {
  max-width: 100%;
}
.blog-content::v-deep img {
  max-width: 100% !important;
  height: auto !important;
}
@media (max-width: 992px) {
  .cws-container .cws-content {
    width: 100%;
  }
}
</style>
